import { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from "../UserContext";
import { API_BASE_URL } from "../config/api";
import { completeLogin as completeSession } from "../lib/session";
import "../pages/Login.css";

// Ships dark until the backend's SSO_ENABLED is on and a tenant has linked.
const SSO_ENABLED = process.env.REACT_APP_SSO_ENABLED === "true";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // MFA step. `mfaToken` is the handle returned by the password step; its
  // presence is what switches this page to the code-entry view.
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState("");
  const [resendIn, setResendIn] = useState(0);

  const navigate = useNavigate();
  const { login } = useContext(UserContext);

  useEffect(() => {
    // match onboarding background
    document.body.classList.add("nx-onboard-body");
    return () => document.body.classList.remove("nx-onboard-body");
  }, []);

  // Count down the resend cooldown so the button re-enables on its own.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // Shared by both the single-step and post-MFA responses, and by the Microsoft
  // sign-in landing page, so the three cannot drift apart. `email` falls back to
  // the form's own state for the rare backend that omits it from the body.
  const completeLogin = async (data) => {
    const next = await completeSession({ email, ...data }, login);
    navigate(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || errorData?.message || "Failed to login");
      }

      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Failed to login");

      // Password was correct but the account requires a second factor. No
      // token is issued yet; switch to the code screen and drop the password.
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setPassword("");
        setNotice(`We sent a 6-digit code to ${data.email || email}.`);
        setResendIn(60);
        return;
      }

      await completeLogin(data);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/login/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ mfa_token: mfaToken, code }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail || errorData?.message || "Invalid verification code"
        );
      }

      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Invalid verification code");

      await completeLogin(data);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || loading) return;
    setLoading(true);
    setError("");
    setNotice("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/login/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ mfa_token: mfaToken }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.detail || data?.message || "Could not resend code");
      }

      // Each resend supersedes the previous code, so track the new handle.
      setMfaToken(data.mfa_token);
      setCode("");
      setNotice("A new code is on its way.");
      setResendIn(60);
    } catch (err) {
      setError(err.message || "Could not resend code.");
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = () => {
    setMfaToken(null);
    setCode("");
    setError("");
    setNotice("");
    setResendIn(0);
  };

  return (
  <div className="nx-login-page">
    <div className="nx-content">
      <div className="nx-login-card">

        {/* Logo inside card */}
        <div className="nx-card-header">
          <img
            src="/images/NexOpta-Logo.png"
            alt="Nexopta Logo"
            className="nx-card-logo"
          />
        </div>

        {error && <div className="nx-alert nx-alert-error">{error}</div>}

        {mfaToken ? (
          <>
            {notice && <div className="nx-alert nx-alert-info">{notice}</div>}

            <form className="nx-login-form" onSubmit={handleVerify}>
              <div className="nx-field">
                <label>Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="000000"
                  className="nx-input"
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="nx-primary-btn"
                disabled={loading || code.length !== 6}
              >
                {loading ? "Verifying..." : "Verify and sign in"}
              </button>
            </form>

            <div className="nx-login-links">
              <p>
                Didn&apos;t get it?{" "}
                <button
                  type="button"
                  className="nx-link-btn"
                  onClick={handleResend}
                  disabled={resendIn > 0 || loading}
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
              </p>
              <p>
                <button
                  type="button"
                  className="nx-link-btn"
                  onClick={backToPassword}
                  disabled={loading}
                >
                  Use a different account
                </button>
              </p>
            </div>
          </>
        ) : (
        <>
        <form className="nx-login-form" onSubmit={handleSubmit}>
          <div className="nx-field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="nx-input"
              required
              disabled={loading}
            />
          </div>

          <div className="nx-field">
            <label>Password</label>

            <div className="nx-password-wrap">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="nx-input"
                required
                disabled={loading}
              />

              <button
                type="button"
                className="nx-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button type="submit" className="nx-primary-btn" disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>

        {SSO_ENABLED && (
          <>
            <div className="nx-sso-divider"><span>or</span></div>

            <button
              type="button"
              className="nx-secondary-btn nx-sso-btn"
              disabled={loading}
              onClick={() => {
                // A full navigation, not fetch(). The browser has to follow
                // Microsoft's redirects and carry its own session cookies;
                // an XHR cannot, and the response is a 302 to another origin.
                window.location.href = `${API_BASE_URL}/api/sso/start/sign-in`;
              }}
            >
              <img src="/images/microsoft-logo.svg" alt="" aria-hidden="true" />
              Sign in with Microsoft
            </button>
          </>
        )}

        <div className="nx-login-links">
          <p>
            Don&apos;t have an account? <Link to="/signup">Sign Up</Link>
          </p>
          <p>
            <Link to="/reset-password">Forgot Password?</Link>
          </p>
        </div>
        </>
        )}
      </div>
    </div>
  </div>
  );
};

export default Login;
